//@ts-check

import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './table';
import styled from '@emotion/styled';
import ThemeButton from 'components/AntdFormInputs/theme-button';
import { AiOutlineDownload } from 'react-icons/ai';
import { useRouter } from 'next/router';
import { noRecord } from 'constants/constants';
import { Skeleton, Tooltip } from 'antd';
import classnames from 'classnames';
import SearchPagination from 'components/Search/SearchPagination';
import FooterTableCustom from 'components/Table/FooterTableCustom';

/**
 * @typedef {import('@tanstack/react-table').RowSelectionState} RowSelectionState
 */

/**
 * @typedef {Object} DataTableProps
 * @property {import('@tanstack/react-table').ColumnDef<any, unknown>[]} columns
 * @property {any[]} data
 * @property {{totalPage: number; currentPage: number; totalItems: number}} [paginationData]
 * @property {React.ReactNode} [children]
 * @property {(value: any[]) => void} [handleDownload]
 * @property {string} [filterClassName]
 * @property {boolean} [isResize]
 * @property {"NODATA" | "IDLE"} [noData]
 * @property {string} [filterLabel]
 * @property {boolean} [showFooter]
 */

const ResizeBox = styled.div`
  position: absolute;
  opacity: 0;
  top: 0;
  right: 0;
  height: 100%;
  width: 4px;
  background-color: gray;
  cursor: col-resize;
  user-select: none;
  touch-action: none;
  border-radius: 6px;

  &.isResizing {
    opacity: 1;
    background-color: gray;
  }
`;

/**
 * DataTable Component
 * @param {DataTableProps} props
 * @returns {JSX.Element}
 */

function DataTable({ columns, data, children, handleDownload, ...props }) {
  /**
   * @type {[import('@tanstack/react-table').ColumnFiltersState, React.Dispatch<React.SetStateAction<import('@tanstack/react-table').ColumnFiltersState>>]}
   */
  const [columnFilters, setColumnFilters] = useState([]);

  /**
   * @type {[RowSelectionState, React.Dispatch<React.SetStateAction<RowSelectionState>>]}
   */

  const [rowSelection, setRowSelection] = useState({});
  const router = useRouter();
  const [pagination, setPagination] = useState({
    pageIndex: (Number(router.query.page) || 1) - 1,
    pageSize: 12,
  });
  const { showFooter = false } = props;
  const table = useReactTable({
    data,
    columns,
    defaultColumn: {
      minSize: 60,
    },
    state: {
      rowSelection,
      pagination,
      columnFilters,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: (/** @type {{ _id: string; }} */ row) => row._id,
    getFilteredRowModel: getFilteredRowModel(),
    getCoreRowModel: getCoreRowModel(),
    onColumnFiltersChange: setColumnFilters,
    columnResizeMode: 'onChange',
  });

  useEffect(() => {
    if (router.query.page || pagination.pageIndex > 0) {
      setPagination((preVal) => ({
        ...preVal,
        pageIndex: (Number(router.query.page) || 1) - 1,
      }));
    }
  }, [router.query.page]);

  useEffect(() => {
    const filterLabel = props.filterLabel;
    const column = table.getColumn(filterLabel);
    if (
      (router.query?.textSearch && filterLabel) ||
      (column?.getFilterValue() && filterLabel)
    ) {
      const filterValue = router.query.textSearch;

      column?.setFilterValue(filterValue);
    }
  }, [router.query?.textSearch, props?.filterLabel]);

  return (
    <>
      <div className="px-1">
        <div className={classnames('mb-1', props?.filterClassName)}>
          {children}
          {props?.filterLabel && (
            // @ts-ignore
            <SearchPagination className="mr-1" />
          )}
          {handleDownload && (
            <Tooltip placement="top" title={'Download'}>
              <ThemeButton
                padding="3px 6px"
                disabled={!data.length}
                onClick={() =>
                  handleDownload(
                    table.getSelectedRowModel().rows.length
                      ? table.getSelectedRowModel().rows
                      : data.map((dt) => ({ original: dt })),
                  )
                }
              >
                <AiOutlineDownload style={{ fontSize: 24 }} />
              </ThemeButton>
            </Tooltip>
          )}
        </div>
        <Table pureCss={`background-color: var(--table-row-bg-clr); color: var(--table-row-text-clr);`}>
          <TableHeader pureCss={'background-color: var(--table-header-bg-clr); color: var(--table-header-text-clr); z-index: 10;'}>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow
                pureCss={`font-size: 14px; color: var(--table-header-text-clr); `}
                key={headerGroup.id}
              >
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      {...{
                        key: header.id,
                        style: {
                          minWidth: header.column.getSize(),
                          maxWidth: Number(header.column.getSize()) + 50,
                        },
                      }}
                      pureCss={`
                     padding: 15px 1.5rem; 
                     position: relative;  
                      border-bottom: 2px solid var(--table-border-clr);
                     border-top: 2px solid var(--table-border-clr);
                     :hover > .resizeTD {
                      opacity: 1;
                    }
                  
                    `}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}

                      {props?.isResize && (
                        <ResizeBox
                          {...{
                            onDoubleClick: () => header.column.resetSize(),
                            onMouseDown: header.getResizeHandler(),
                            onTouchStart: header.getResizeHandler(),
                            className: `resizeTD ${
                              header.column.getIsResizing() ? 'isResizing' : ''
                            }`,
                          }}
                        />
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody
            pureCss={`
            & > tr:not(:last-child)  {
               border-bottom: 1px solid var(--table-border-clr);
            }
           
          `}
          >
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  pureCss={`
                  font-size: 14px; 
                  color: var(--table-row-text-clr);
                  font-weight: 400;
                  background-color: var(--table-row-bg-clr);
                  &:hover {
                    background-color: var(--table-row-hover-bg-clr)
                  }
                  `}
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      pureCss={`
                      padding: 10px 1.5rem; 
                      `}
                      style={{
                        minWidth: cell.column.getSize(),
                        maxWidth: Number(cell.column.getSize()) + 50,
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  {props.noData === 'IDLE' && !data?.length
                    ? [...Array(11)].map((_, index) => (
                        <Skeleton.Input
                          key={index}
                          active
                          size="large"
                          className="w-200  rounded m-y-5"
                          style={{ display: 'table', height: 30 }}
                        />
                      ))
                    : noRecord}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {showFooter ? (
        <FooterTableCustom
          pageCount={table.getPageCount()}
          currentPage={table.getState().pagination.pageIndex + 1}
          totalItems={table.getPrePaginationRowModel().rows.length}
          currentItem={table.getRowModel().rows.length}
          isBusy={false}
          changePath
          showInputSearch={false}
          onChange={() => {}}
        />
      ) : null}
    </>
  );
}

export default DataTable;
